// src/pages/DeleteAccount.jsx

import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  Button,
  Typography,
  Steps,
  Modal,
  Alert,
  Result,
  Space,
} from "antd";
import {
  ExclamationCircleOutlined,
  PhoneOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Trash2 } from "lucide-react";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Step } = Steps;
const { confirm } = Modal;

const DeleteAccount = () => {
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const { request } = useRequest();
  const navigate = useNavigate();

  const handleSendOTPConfirm = () => {
    confirm({
      title: "Confirm Account Deletion",
      icon: <ExclamationCircleOutlined />,
      content:
        "This action is permanent. All your data will be removed permanently.",
      okText: "Yes, Send OTP",
      okType: "danger",
      centered: true,
      onOk() {
        handleSendOTP();
      },
    });
  };

  const handleSendOTP = () => {
    const values = form.getFieldsValue();

    if (!values.mobile || !values.country_code) {
      ShowToast("Please enter mobile number", Severty.ERROR);
      return;
    }

    setLoading(true);

    request({
      url: "/auth/send-otp",
      method: "POST",
      data: {
        mobile: values.mobile,
        country_code: values.country_code,
        purpose: "DELETE",
      },
      onSuccess: (res) => {
        ShowToast(res?.data?.message, Severty.SUCCESS);
        setOtpSent(true);
        setCurrentStep(1);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || "Failed to send OTP",
          Severty.ERROR
        );
        setLoading(false);
      },
    });
  };

  const handleVerifyOTP = (values) => {
    setLoading(true);

    request({
      url: "/auth/verify-otp",
      method: "POST",
      data: {
        mobile: values.mobile,
        country_code: values.country_code,
        otp: values.otp,
      },
      onSuccess: (res) => {
        ShowToast(res?.data?.message, Severty.SUCCESS);
        setCurrentStep(2);
        setIsDeleted(true);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(
          err?.response?.data?.message || "Invalid OTP",
          Severty.ERROR
        );
        setLoading(false);
      },
    });
  };

  return (
    <div style={{ padding: "60px 20px", background: "#f5f7fa", minHeight: "100vh" }}>
      {!isDeleted ? (
        <Row justify="center">
          <Col xs={24} sm={22} md={16} lg={10}>
            <Card
              bordered={false}
              style={{
                borderRadius: "16px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              }}
              bodyStyle={{ padding: "40px" }}
            >
              {/* Header */}
              <div style={{ textAlign: "center", marginBottom: 30 }}>
                <Trash2 size={42} color="#ff4d4f" />
                <Title level={3} style={{ marginTop: 15 }}>
                  Delete Account
                </Title>
                <Text type="secondary">
                  Verify via OTP to permanently delete your account
                </Text>
              </div>

              {/* Steps */}
              <Steps current={currentStep} size="small" style={{ marginBottom: 30 }}>
                <Step title="Enter Mobile" />
                <Step title="Verify OTP" />
                <Step title="Deleted" />
              </Steps>

              {/* Warning Box */}
              <Alert
                message="Warning"
                description="Deleting your account will permanently remove all your data. This action cannot be undone."
                type="warning"
                showIcon
                style={{ marginBottom: 25 }}
              />

              <Form form={form} layout="vertical" onFinish={handleVerifyOTP}>
                <Form.Item
                  label="Country Code"
                  name="country_code"
                  rules={[{ required: true, message: "Enter country code" }]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="91"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Mobile Number"
                  name="mobile"
                  rules={[{ required: true, message: "Enter mobile number" }]}
                >
                  <Input
                    prefix={<PhoneOutlined />}
                    placeholder="Enter mobile number"
                    size="large"
                  />
                </Form.Item>

                {otpSent && (
                  <Form.Item
                    label="Enter OTP"
                    name="otp"
                    rules={[{ required: true, message: "Enter OTP" }]}
                  >
                    <Input.Password
                      prefix={<LockOutlined />}
                      placeholder="Enter OTP"
                      size="large"
                    />
                  </Form.Item>
                )}

                <Form.Item style={{ marginTop: 20 }}>
                  {!otpSent ? (
                    <Button
                      danger
                      type="primary"
                      block
                      size="large"
                      loading={loading}
                      onClick={handleSendOTPConfirm}
                    >
                      Send OTP
                    </Button>
                  ) : (
                    <Button
                      danger
                      type="primary"
                      block
                      size="large"
                      htmlType="submit"
                      loading={loading}
                    >
                      Verify & Delete Account
                    </Button>
                  )}
                </Form.Item>
              </Form>
            </Card>
          </Col>
        </Row>
      ) : (
        <Result
          status="success"
          title="Account Deleted Successfully"
          subTitle="Your account has been permanently removed."
          extra={[
            <Space key="btn">
              <Button type="primary" onClick={() => navigate("/user/delete")}>
                Delete More Account
              </Button>
            </Space>,
          ]}
        />
      )}
    </div>
  );
};

export default DeleteAccount;